import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import loyaltyService from '../../../services/loyaltyService';

const RedemptionHistory = ({ userId }) => {
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRedemption, setSelectedRedemption] = useState(null);

  // Load redemption history
  useEffect(() => {
    const loadRedemptions = async () => {
      if (!userId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const { data, error } = await loyaltyService?.getRedemptionHistory(userId);
        
        if (error) {
          setError(error?.message || 'Failed to load redemption history');
        } else {
          setRedemptions(data || []);
        }
      } catch (err) {
        setError('Failed to connect to redemption service');
      } finally {
        setLoading(false);
      }
    };

    loadRedemptions();
  }, [userId]);

  // Filter redemptions by status
  const filteredRedemptions = redemptions?.filter(redemption => {
    if (statusFilter === 'all') return true;
    return redemption?.status === statusFilter;
  });

  // Get status display info
  const getStatusDisplay = (status) => {
    const displays = {
      'pending': {
        label: 'Pending',
        icon: 'Clock',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        description: 'Your redemption is being processed'
      },
      'approved': {
        label: 'Approved',
        icon: 'CheckCircle',
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        description: 'Redemption approved, preparing for fulfillment'
      },
      'fulfilled': {
        label: 'Fulfilled',
        icon: 'Check',
        color: 'text-success',
        bgColor: 'bg-success/20',
        description: 'Redemption completed successfully'
      },
      'cancelled': {
        label: 'Cancelled',
        icon: 'X',
        color: 'text-destructive',
        bgColor: 'bg-destructive/20',
        description: 'Redemption was cancelled'
      },
      'refunded': {
        label: 'Refunded',
        icon: 'RefreshCw',
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
        description: 'Tokens have been refunded to your wallet'
      }
    };
    return displays?.[status] || displays?.pending;
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    const icons = {
      'wellness_products': 'Heart',
      'marketplace_discount': 'Percent',
      'workshop_access': 'BookOpen',
      'consultation_time': 'Users',
      'premium_content': 'Star',
      'merchandise': 'ShoppingBag',
      'gift_card': 'Gift'
    };
    return icons?.[category] || 'Gift';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">My Redemptions</h2>
          <p className="text-sm text-muted-foreground">
            Track your reward redemptions and their status
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-foreground">Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="fulfilled">Fulfilled</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
      </div>
      {/* Error State */}
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center space-x-3">
          <Icon name="AlertTriangle" size={20} className="text-destructive" />
          <div>
            <p className="text-sm font-medium text-destructive">Error loading redemptions</p>
            <p className="text-sm text-destructive/80">{error}</p>
          </div>
        </div>
      )}
      {/* Loading State */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)]?.map((_, i) => (
            <div key={i} className="bg-card rounded-lg border border-border p-6 animate-pulse">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-muted rounded-lg"></div>
                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="h-4 bg-muted rounded w-48"></div>
                      <div className="h-3 bg-muted rounded w-32"></div>
                    </div>
                    <div className="w-20 h-6 bg-muted rounded-full"></div>
                  </div>
                  <div className="h-3 bg-muted rounded w-64"></div>
                  <div className="flex space-x-4">
                    <div className="h-3 bg-muted rounded w-24"></div>
                    <div className="h-3 bg-muted rounded w-32"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredRedemptions?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="ShoppingBag" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Redemptions Found</h3>
          <p className="text-muted-foreground mb-4">
            {statusFilter !== 'all' 
              ? `No redemptions with status "${statusFilter}"`
              : 'You haven\'t redeemed any rewards yet'
            }
          </p>
          {statusFilter === 'all' && (
            <button
              onClick={() => {}} // Navigate to rewards catalog
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Browse Rewards
            </button>
          )}
        </div>
      ) : (
        /* Redemptions List */
        (<div className="space-y-4">
          {filteredRedemptions?.map((redemption) => {
            const statusDisplay = getStatusDisplay(redemption?.status);
            const reward = redemption?.reward;
            
            return (
              <div
                key={redemption?.id}
                className="bg-card rounded-lg border border-border p-6 shadow-wellness hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedRedemption(redemption)}
              >
                <div className="flex items-start space-x-4">
                  {/* Reward Icon/Image */}
                  <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                    {reward?.image_url ? (
                      <img
                        src={reward?.image_url}
                        alt={reward?.title}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <Icon
                        name={getCategoryIcon(reward?.category)}
                        size={24}
                        className="text-muted-foreground"
                      />
                    )}
                  </div>

                  {/* Redemption Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">
                          {reward?.title || 'Unknown Reward'}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Quantity: {redemption?.quantity} • Redeemed {formatDate(redemption?.redeemed_at)}
                        </p>
                      </div>
                      <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${statusDisplay?.bgColor} ${statusDisplay?.color}`}>
                        <Icon name={statusDisplay?.icon} size={12} />
                        <span>{statusDisplay?.label}</span>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {reward?.description || 'No description available'}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center space-x-1">
                          <Icon name="Coins" size={14} className="text-primary" />
                          <span className="font-medium text-primary">
                            {loyaltyService?.formatTokens(redemption?.total_tokens)} tokens
                          </span>
                        </div>
                        
                        {redemption?.fulfilled_at && (
                          <div className="flex items-center space-x-1 text-muted-foreground">
                            <Icon name="Calendar" size={14} />
                            <span>Fulfilled {formatDate(redemption?.fulfilled_at)}</span>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          setSelectedRedemption(redemption);
                        }}
                        className="text-sm text-primary hover:text-primary/80 transition-colors"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>)
      )}
      {/* Redemption Details Modal */}
      {selectedRedemption && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Redemption Details</h3>
                <button
                  onClick={() => setSelectedRedemption(null)}
                  className="p-1 hover:bg-muted rounded-lg transition-colors"
                >
                  <Icon name="X" size={20} className="text-muted-foreground" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Reward Info */}
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon
                      name={getCategoryIcon(selectedRedemption?.reward?.category)}
                      size={20}
                      className="text-muted-foreground"
                    />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">
                      {selectedRedemption?.reward?.title || 'Unknown Reward'}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {selectedRedemption?.reward?.description}
                    </p>
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center justify-between py-2 border-t border-b border-border">
                  <span className="font-medium text-foreground">Status:</span>
                  <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-sm font-medium ${getStatusDisplay(selectedRedemption?.status)?.bgColor} ${getStatusDisplay(selectedRedemption?.status)?.color}`}>
                    <Icon name={getStatusDisplay(selectedRedemption?.status)?.icon} size={14} />
                    <span>{getStatusDisplay(selectedRedemption?.status)?.label}</span>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Quantity:</span>
                    <span className="font-medium text-foreground">{selectedRedemption?.quantity}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Tokens:</span>
                    <span className="font-medium text-primary">
                      {loyaltyService?.formatTokens(selectedRedemption?.total_tokens)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Redeemed:</span>
                    <span className="font-medium text-foreground">
                      {formatDate(selectedRedemption?.redeemed_at)}
                    </span>
                  </div>
                  
                  {selectedRedemption?.fulfilled_at && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Fulfilled:</span>
                      <span className="font-medium text-foreground">
                        {formatDate(selectedRedemption?.fulfilled_at)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Redemption Details */}
                {selectedRedemption?.redemption_details && Object.keys(selectedRedemption?.redemption_details)?.length > 0 && (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <h5 className="text-sm font-medium text-foreground mb-2">Additional Details:</h5>
                    <div className="space-y-1 text-sm">
                      {selectedRedemption?.redemption_details?.discount_code && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Discount Code:</span>
                          <code className="font-mono text-primary bg-primary/10 px-2 py-1 rounded">
                            {selectedRedemption?.redemption_details?.discount_code}
                          </code>
                        </div>
                      )}
                      {selectedRedemption?.redemption_details?.expires_at && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Expires:</span>
                          <span className="text-foreground">
                            {formatDate(selectedRedemption?.redemption_details?.expires_at)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedRedemption?.notes && (
                  <div className="p-3 bg-accent/10 rounded-lg">
                    <h5 className="text-sm font-medium text-foreground mb-1">Notes:</h5>
                    <p className="text-sm text-muted-foreground">{selectedRedemption?.notes}</p>
                  </div>
                )}

                {/* Status Description */}
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    {getStatusDisplay(selectedRedemption?.status)?.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RedemptionHistory;